/* eslint-disable max-len */
import {DelayParameter} from '@frontapp/workflows/rules/parameters';
import React, {FC} from 'react';
import styled from 'styled-components';

import {UnreachableError} from '../../../../libs/shared/core/src/helpers/types/customErrors';
import {TeamModel} from '../../../../libs/shared/core/src/models/api/teamModel';
import {CircleModel} from '../../../models/api/circleModel';
import {DepartmentsEnum} from '../../../models/api/userMetadataModel';
import {SettingsInboxesNewCreatorState} from '../../../settings/inboxes/newCreator/settingsInboxesNewCreatorState';
import {OnboardingInboxVisual, OnboardingInboxVisualInboxPosition} from './onboardingInboxVisual';
import {OnboardingTeammateGroupVisual} from './onboardingTeammateGroupVisual';
import {OnboardingInboxVisualFeature} from './workflows/onboardingInboxIllustration';

/*
 * Props.
 */

export enum OnboardingAdminWizardVisualFrame {
  TEAMMATE_GROUPS = 'teammate_groups',
  INBOX = 'inbox',
  GOALS = 'goals',
  SLA_RULE = 'sla_rule',
  TAG_RULE = 'tag_rule',
  ASSIGN_RULE = 'assign_rule',
  CREATED_RESOURCES = 'created_resources',
}

interface OnboardingAdminWizardVisualProps {
  frame: OnboardingAdminWizardVisualFrame;

  selectedTeamType: DepartmentsEnum | string;
  teamDisplayName: string;
  initialHasTeamType: boolean;

  companyName: string;
  inboxState: SettingsInboxesNewCreatorState;
  createdCircle: CircleModel | undefined;

  team: Pick<TeamModel, 'name' | 'color'>;

  position: OnboardingInboxVisualInboxPosition | undefined;
  previousPosition: OnboardingInboxVisualInboxPosition | undefined;

  slaRuleSlaReplyTimeGoal: DelayParameter | undefined;

  tagRuleTagName: string;
  tagRuleKeywords: ReadonlyArray<string>;
}

/*
 * Style.
 */

const StyledContainerDiv = styled.div`
  width: 542px;
  height: 692px;
`;

const StyledTeammateGroupsContainerDiv = styled.div`
  flex: 0 0 50%;
  align-items: center;
  display: flex;
  flex-direction: column;
  justify-content: center;
  height: 100%;
  background: ${(p) => p.theme.greys.shade20};
`;

/*
 * Component.
 */

export const OnboardingAdminWizardVisual: FC<OnboardingAdminWizardVisualProps> = (props) => (
  <StyledContainerDiv>{renderFrame(props)}</StyledContainerDiv>
);

function renderFrame(props: OnboardingAdminWizardVisualProps) {
  const {
    frame,
    position,
    previousPosition,

    selectedTeamType,
    teamDisplayName,
    initialHasTeamType,

    inboxState,
    companyName,
    createdCircle,

    team,

    slaRuleSlaReplyTimeGoal,

    tagRuleTagName,
    tagRuleKeywords,
  } = props;

  const {name, color, circleIds} = inboxState;
  const circleName = circleIds.length > 0 && createdCircle ? createdCircle.name : '';

  switch (frame) {
    case OnboardingAdminWizardVisualFrame.TEAMMATE_GROUPS:
      return (
        <StyledTeammateGroupsContainerDiv>
          <OnboardingTeammateGroupVisual
            hasSelectedTeamType={selectedTeamType !== ''}
            selection={teamDisplayName}
            shouldAnimate={!initialHasTeamType}
          />
        </StyledTeammateGroupsContainerDiv>
      );
    case OnboardingAdminWizardVisualFrame.INBOX:
      return (
        <OnboardingInboxVisual
          circleName={circleName}
          inboxName={name}
          color={color}
          companyName={companyName}
          position={position}
          previousPosition={previousPosition}
        />
      );
    case OnboardingAdminWizardVisualFrame.GOALS:
      return (
        <OnboardingInboxVisual
          inboxName={name}
          color={color}
          companyName={companyName}
          isCircleVisualVisible={false}
          position={position}
          previousPosition={previousPosition}
        />
      );
    case OnboardingAdminWizardVisualFrame.SLA_RULE:
      return (
        <OnboardingInboxVisual
          inboxName={name}
          color={color}
          companyName={companyName}
          isCircleVisualVisible={false}
          feature={OnboardingInboxVisualFeature.SLA_RULE}
          position={position}
          previousPosition={previousPosition}
          slaRuleSlaReplyTimeGoal={slaRuleSlaReplyTimeGoal}
        />
      );
    case OnboardingAdminWizardVisualFrame.TAG_RULE:
      return (
        <OnboardingInboxVisual
          inboxName={name}
          color={color}
          companyName={companyName}
          isCircleVisualVisible={false}
          feature={OnboardingInboxVisualFeature.TAG_RULE}
          position={position}
          previousPosition={previousPosition}
          tagRuleTagName={tagRuleTagName}
          tagRuleKeywords={tagRuleKeywords}
        />
      );
    case OnboardingAdminWizardVisualFrame.ASSIGN_RULE:
      return (
        <OnboardingInboxVisual
          inboxName={name}
          color={color}
          companyName={companyName}
          circleName={circleName}
          isCircleVisualVisible={false}
          feature={OnboardingInboxVisualFeature.ASSIGN_RULE}
          position={position}
          previousPosition={previousPosition}
        />
      );
    case OnboardingAdminWizardVisualFrame.CREATED_RESOURCES:
      return (
        <OnboardingInboxVisual
          inboxName={name}
          color={color}
          companyName={companyName}
          isCircleVisualVisible={false}
          team={team}
          position={position}
          previousPosition={previousPosition}
          inboxAnimation
        />
      );
    default:
      throw new UnreachableError(frame);
  }
}
